import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Camera, Activity, MapPin, FolderOpen, ExternalLink, X, Sparkles, BellDot, BellOff, CheckCheck, Maximize2, Minimize2 } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import type { CameraMapPin, Event } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useQuery } from '@tanstack/react-query'
import { camerasApi, eventsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { cn, formatCardDateTime } from '@/lib/utils'
import { EventDetailModal } from '@/components/events/EventDetailModal'
import { RecentEventPreview } from '@/components/events/RecentEventPreview'
import { UnreadDot } from '@/components/events/UnreadDot'
import { useDisplayPrefs } from '@/store/displayPrefsStore'  // #301
import { useMarkAllEventsRead } from '@/hooks/useMarkEventRead'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useImageRetry } from '@/hooks/useImageRetry'  // #336
import { CriticalEventRow } from '@/components/events/CriticalEventRow'  // #294
import { SuspiciousEventRow } from '@/components/events/SuspiciousEventRow'  // #406
import { useMarkSeen, useOpenSuspicious } from '@/hooks/useSuspiciousEvents'  // #406
import { isOpenSuspicious, isSuspiciousEvent } from '@/lib/suspiciousEvents'  // #413
import { useSuspiciousBeta } from '@/hooks/useSuspiciousBeta'  // #408
import { useOpenCriticals } from '@/hooks/useCriticalAlerts'  // #294
import { useAcknowledgeCritical } from '@/hooks/useAcknowledgeCritical'  // #294
import { isCriticalEvent, isOpenCritical, pinOpenCriticals } from '@/lib/criticalEvents'  // #294
import { useEventTitle } from '@/hooks/useEventTitle'  // #294
import { toast } from 'sonner'
import { useDashboardMapAnchor } from '@/contexts/DashboardMapAnchorContext'
import { LocationDetails } from '@/components/maps/LocationDetails'
import { FleetMapFilters } from '@/components/maps/FleetMapFilters'
import { useMapSettings } from '@/hooks/useMapSettings'
import { filterPins, groupsOf, isFiltering, type MapPinFilter } from '@/lib/mapPinFilter'
import { loadMapView, saveMapFilter } from '@/lib/dashboardMapView'
import { muteReason, type MuteReason } from '@/lib/pushBell'  // #337
import { usePushMutes } from '@/hooks/usePushMutes'  // #337
import { useAlertLinked } from '@/store/alertLinkStore'  // #413

/** #337 — the reason text of each push mute (notifications namespace) */
const MUTE_REASON_KEY = {
  'camera-off': 'muted.cameraOff',
  'camera-filter': 'muted.cameraFilter',
  'user-filter': 'muted.userFilter',
  'my-camera-choice': 'muted.myCameraChoice',   // CP 391
} as const satisfies Record<MuteReason, string>

/** #413 — the Regular row's wrapper: the row a toast points at (data-alert-linked) and "Show in list" scrolls to. */
// forwardRef + rest: RecentEventPreview's PopoverAnchor asChild hands this component its ref and handlers
const AlertRowShell = forwardRef<HTMLDivElement, { eventId: number } & HTMLAttributes<HTMLDivElement>>(
  function AlertRowShell({ eventId, children, className, ...rest }, ref) {
    const linked = useAlertLinked(eventId)
    return (
      <div
        ref={ref}
        {...rest}
        className={cn('relative rounded-lg', className)}
        data-alert-row={eventId}
        data-alert-linked={linked ? '' : undefined}
      >
        {children}
      </div>
    )
  },
)

/**
 * #337 — a Recent events row whose event the push mutes silenced (no toast, no chime) says so: a small bell-off
 * mark named by the reason (tooltip and screen reader), opening Settings → Notifications.
 * Fix round 1 (review I-2, I-3): a link BESIDE the row button, never inside it (a link inside a button is invalid HTML,
 * its focus opened the row's hover card: the CriticalEventRow pattern), placed on the top-left corner of the row's
 * picture, before the title, so a title cut off with "…" never hides it (as #292 did for the unread badge). The
 * picture is centred in the row (64 px high, 80 px from sm, 10 px from the left), so the mark sits 4 px inside it.
 * CP 391 (fix round 391 p) — a mute by the person's own choice for this camera ("My phone alerts") names that choice
 * and is no link: Settings does not show per-camera choices (they are on the camera's page). Same place and look.
 */
function MutedMark({ reason, className }: { reason: MuteReason | null; className?: string }) {
  const { t } = useTranslation('notifications')
  if (!reason) return null
  const label = t(MUTE_REASON_KEY[reason])
  if (reason === 'my-camera-choice') {
    return (
      <span
        role="img"
        title={label}
        aria-label={label}
        className={cn(
          'absolute left-3.5 top-[calc(50%-28px)] sm:top-[calc(50%-36px)] z-10 inline-flex rounded-full bg-background/90 p-1',
          'text-muted-foreground shadow-sm ring-1 ring-border',
          className,
        )}
      >
        <BellOff className="h-3.5 w-3.5" aria-hidden />
      </span>
    )
  }
  return (
    <Link
      to="/settings?tab=notifications"
      title={label}
      aria-label={label}
      className={cn(
        'absolute left-3.5 top-[calc(50%-28px)] sm:top-[calc(50%-36px)] z-10 inline-flex rounded-full bg-background/90 p-1',
        'text-muted-foreground shadow-sm ring-1 ring-border hover:text-foreground',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        className,
      )}
    >
      <BellOff className="h-3.5 w-3.5" aria-hidden />
    </Link>
  )
}

// Relative time — uses i18n translation keys
function useRelativeTime() {
  const { t } = useTranslation('common')
  return (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const s = Math.floor(diff / 1000)
    if (s < 60) return t('time.justNow')
    const m = Math.floor(s / 60)
    if (m < 60) return t('time.minutesAgo', { count: m })
    const h = Math.floor(m / 60)
    if (h < 24) return t('time.hoursAgo', { count: h })
    const d = Math.floor(h / 24)
    return t('time.daysAgo', { count: d })
  }
}

// Animates a number from its previous value to `target` with ease-out cubic.
// On first render (initialLoad = true) it counts up from 0.
// On subsequent poll updates it counts from the previous displayed value.
function useCountUp(target: number, duration = 700, enabled = true) {
  const [value, setValue] = useState(target)
  const from = useRef(target)
  const raf = useRef<number>()

  useEffect(() => {
    if (raf.current) cancelAnimationFrame(raf.current)
    if (!enabled) { setValue(target); from.current = target; return }
    const start = from.current
    if (start === target) return
    const delta = target - start
    const began = performance.now()
    const animate = (now: number) => {
      const progress = Math.min((now - began) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(start + eased * delta))
      if (progress < 1) {
        raf.current = requestAnimationFrame(animate)
      } else {
        from.current = target
      }
    }
    raf.current = requestAnimationFrame(animate)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [target, duration, enabled])

  return value
}

interface StatCardProps {
  title: string
  value: string | number
  description: string
  icon: React.ReactNode
  accent: string
  iconColor: string
  topBorder: string
  loading?: boolean
  delay?: string
  /** #393 — phones: one slim row (colour bar, icon, title; number + description on the right) */
  slim?: boolean
}

function StatCard({ title, value, description, icon, accent, iconColor, topBorder, loading, delay, slim }: StatCardProps) {
  const numericValue = typeof value === 'number' ? value : (!isNaN(Number(value)) ? Number(value) : null)
  const counted = useCountUp(numericValue ?? 0, 700, !loading && numericValue !== null)
  const displayValue = loading ? null : (numericValue !== null ? counted : value)

  if (slim) {
    return (
      <Card data-stat-card="phone" className={cn('animate-fade-up overflow-hidden flex items-center gap-2 pr-3', delay)}>
        <div data-stat-bar="" className={cn('w-1 shrink-0 self-stretch', topBorder)} />
        <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', accent, iconColor)}>
          {icon}
        </div>
        <h3 data-stat-title="" className="min-w-0 flex-1 truncate whitespace-nowrap text-[13px] font-medium text-muted-foreground">
          {title}
        </h3>
        {loading ? (
          <div className="shimmer my-4 h-6 w-14 rounded" />
        ) : (
          <div className="flex flex-col items-end py-2.5 leading-tight">
            <span data-stat-value="" className="text-xl font-semibold leading-none tabular-nums">{displayValue}</span>
            <span data-stat-description="" className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">{description}</span>
          </div>
        )}
      </Card>
    )
  }

  return (
    <Card data-stat-card="wide" className={cn('animate-fade-up overflow-hidden', delay)}>
      <div className={cn('h-0.5 w-full', topBorder)} />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4">
        <CardTitle className="text-body font-medium text-muted-foreground">{title}</CardTitle>
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', accent, iconColor)}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <>
            <div className="shimmer h-8 w-16 rounded mb-1" />
            <div className="shimmer h-3 w-24 rounded" />
          </>
        ) : (
          <>
            <div className="text-heading">{displayValue}</div>
            <p className="text-label text-muted-foreground mt-0.5">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// #292 — "Recent Events": how many events this person has not opened (per person). Replaces the
// old "Active Cameras" card. The number links to the Events page filtered to unread events (more
// than the 20 rows on this page), on the All tab: the count covers every category, and the user's
// default tab would hide some. "Mark all as read" sets it to 0. A count that cannot be loaded
// shows a dash, never a number that may be wrong.
// #304 — like the list, the count leaves motion-only events out (server side), so the link adds
// hide_motion=true and the Events page shows the same events.
// #368 — the unread count and "Mark all as read", shared by the card (below xl) and the strip (from xl).
function useUnreadEvents() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.events.unreadCount(),
    queryFn: () => eventsApi.getUnreadCount(),
    ...queryOptions.events.unreadCount,
  })
  const markAll = useMarkAllEventsRead()
  const count = data?.count
  const value = count !== undefined ? count : isError ? '—' : null
  return { count, value, isLoading, markAll }
}

function UnreadEventsCard({ slim }: { slim?: boolean }) {
  const { t } = useTranslation('cameras')
  const { count, value, isLoading, markAll } = useUnreadEvents()

  if (slim) {
    // #393 — the slim row: the number column is the same as the other rows' (right edge), the icon-only
    // "Mark all as read" sits just left of it, so the digits of all three rows line up.
    return (
      <Card data-testid="unread-events-card" data-stat-card="phone" className="animate-fade-up overflow-hidden delay-150 flex items-center gap-2 pr-3">
        <div data-stat-bar="" className="w-1 shrink-0 self-stretch bg-rose-500" />
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
          <BellDot className="h-4 w-4" />
        </div>
        <h3 data-stat-title="" className="min-w-0 flex-1 truncate whitespace-nowrap text-[13px] font-medium text-muted-foreground">
          {t('dashboard.recentEvents')}
        </h3>
        {isLoading ? (
          <div className="shimmer my-4 h-6 w-14 rounded" />
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 shrink-0 px-0"
              disabled={!count || markAll.isPending}
              onClick={() => markAll.mutate()}
              aria-label={t('dashboard.markAllRead')}
              title={t('dashboard.markAllRead')}
            >
              <CheckCheck className="h-3.5 w-3.5" />
            </Button>
            <Link
              to="/events?unread=true&category=all&hide_motion=true"
              title={t('dashboard.viewUnread')}
              className="flex flex-col items-end py-2.5 leading-tight rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            >
              <span data-stat-value="" className="text-xl font-semibold leading-none tabular-nums">{value}</span>
              <span data-stat-description="" className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">{t('dashboard.unreadDescription')}</span>
            </Link>
          </>
        )}
      </Card>
    )
  }

  return (
    <Card data-testid="unread-events-card" data-stat-card="wide" className="animate-fade-up overflow-hidden delay-150">
      <div className="h-0.5 w-full bg-rose-500" />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4">
        <CardTitle className="text-body font-medium text-muted-foreground">{t('dashboard.recentEvents')}</CardTitle>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
          <BellDot className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <>
            <div className="shimmer h-8 w-16 rounded mb-1" />
            <div className="shimmer h-3 w-24 rounded" />
          </>
        ) : (
          <div className="flex items-end justify-between gap-2">
            <Link
              to="/events?unread=true&category=all&hide_motion=true"
              title={t('dashboard.viewUnread')}
              className="min-w-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <div className="text-heading tabular-nums">{value}</div>
              <p className="text-label text-muted-foreground mt-0.5 truncate">{t('dashboard.unreadDescription')}</p>
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 flex-shrink-0 px-2 text-xs"
              disabled={!count || markAll.isPending}
              onClick={() => markAll.mutate()}
              aria-label={t('dashboard.markAllRead')}
              title={t('dashboard.markAllRead')}
            >
              <CheckCheck className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">{t('dashboard.markAllRead')}</span>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// #368 — from xl the three cards become one 40 px strip: the same figures from the same queries (the page passes
// the camera and event totals it already has; the unread figure comes from the shared hook), the unread figure
// still links to the unread events and still has "Mark all as read". No count-up here; the cards keep theirs.
function StripFigure({ icon, label, value, loading }: { icon: React.ReactNode; label: string; value: number; loading: boolean }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {icon}
      <span className="truncate text-label text-muted-foreground">{label}</span>
      {loading ? <span className="shimmer h-4 w-8 rounded" /> : <span className="text-sm font-semibold tabular-nums">{value}</span>}
    </div>
  )
}

function StatsStrip({ totalCameras, camerasLoading, totalEvents, totalLoading, hidden }: {
  totalCameras: number; camerasLoading: boolean; totalEvents: number; totalLoading: boolean
  /** The map is expanded: the strip gives its row to the map. */
  hidden: boolean
}) {
  const { t } = useTranslation('cameras')
  const { count, value, isLoading, markAll } = useUnreadEvents()
  return (
    <div
      data-testid="stats-strip"
      className={cn('hidden xl:flex h-10 shrink-0 items-center gap-6 rounded-lg border bg-card px-4 text-card-foreground shadow-elevation-1', hidden && 'xl:hidden')}
    >
      <StripFigure icon={<Camera className="h-4 w-4 text-primary" />} label={t('dashboard.totalCameras')} value={totalCameras} loading={camerasLoading} />
      <div className="h-5 w-px bg-border" />
      <div className="flex min-w-0 items-center gap-2">
        <BellDot className="h-4 w-4 text-rose-600 dark:text-rose-400" />
        <Link
          to="/events?unread=true&category=all&hide_motion=true"
          title={t('dashboard.viewUnread')}
          className="flex min-w-0 items-center gap-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="truncate text-label text-muted-foreground">{t('dashboard.recentEvents')}</span>
          {isLoading ? <span className="shimmer h-4 w-8 rounded" /> : <span className="text-sm font-semibold tabular-nums">{value}</span>}
        </Link>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 flex-shrink-0 px-2 text-xs"
          disabled={!count || markAll.isPending}
          onClick={() => markAll.mutate()}
          aria-label={t('dashboard.markAllRead')}
          title={t('dashboard.markAllRead')}
        >
          <CheckCheck className="mr-1 h-3.5 w-3.5" />
          {t('dashboard.markAllRead')}
        </Button>
      </div>
      <div className="h-5 w-px bg-border" />
      <StripFigure icon={<Activity className="h-4 w-4 text-amber-600 dark:text-amber-400" />} label={t('dashboard.totalEvents')} value={totalEvents} loading={totalLoading} />
    </div>
  )
}

/**
 * #294 redesign (CP 294 Section 1.3) — the ids of open critical events that were first seen AFTER the page's
 * first load (they get the 2 s arrival glow; the ones already open when the page loaded do not, and neither
 * does a page re-opened with cached data). `ready` = the lists have loaded once. Ids only ever join the set,
 * so a re-render never replays the glow.
 */
function useJustArrived(ids: number[], ready: boolean): ReadonlySet<number> {
  const seen = useRef<Set<number> | null>(null)
  const arrived = useRef(new Set<number>())
  if (ready) {
    if (seen.current === null) {
      seen.current = new Set(ids)
    } else {
      for (const id of ids) {
        if (!seen.current.has(id)) {
          seen.current.add(id)
          arrived.current.add(id)
        }
      }
    }
  }
  return arrived.current
}

export function DashboardPage() {
  const { t } = useTranslation('cameras')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const relativeTime = useRelativeTime()
  const navigate = useNavigate()
  const [selectedPin, setSelectedPin] = useState<CameraMapPin | null>(null)

  // Persistent map anchor — publishes a DOM ref so PersistentDashboardMap
  // (mounted once at MainLayout level) can portal <MapView> into it.
  const { setAnchor, setModeAnchor, setOnMarkerClick, setVisibleIds, setSelectedId, noteFilterEdit } = useDashboardMapAnchor()
  // #290 — the filter comes back with the map after leaving the dashboard (this tab only).
  const [pinFilter, setPinFilter] = useState(() => loadMapView().filter)
  const editPinFilter = useCallback((next: MapPinFilter) => {
    setPinFilter(next)
    saveMapFilter(next)
    noteFilterEdit()   // ends the restored map view's hold: the pins this edit shows are fitted
  }, [noteFilterEdit])
  const mapSettings = useMapSettings()   // #287 — the legend follows the company's pin colour and offline mark

  const { data: camerasPage, isLoading: camerasLoading } = useQuery({
    queryKey: queryKeys.cameras.summary(),
    queryFn: () => camerasApi.getPaginated(1),
    ...queryOptions.cameras.all,
  })

  const totalCameras = camerasPage?.count ?? 0
  // #393 — phones (under 640 px): the three cards as slim one-line rows
  const phoneCards = !useMediaQuery('(min-width: 640px)', true)

  // #304 — Recent Events leaves motion-only events out, on the server (before paging, so the
  // list still has 20 rows); a live event refetches it, so a motion event never slips in.
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: queryKeys.events.recent(),
    queryFn: () => eventsApi.getAll({ hide_motion: true }, 1),
    ...queryOptions.events.recent,
  })
  // #336 — a row's expired thumbnail refreshes the recent rows once (fresh links); the row keeps its look meanwhile
  const { onError: onRecentThumbError } = useImageRetry(queryKeys.events.recent())

  // #304 — Total Events still counts every event, motion included (owner: "Everything (as
  // today)"): its own light query — one row, the count counted server-side over the whole scope.
  const { data: totalEventsCount, isLoading: totalLoading } = useQuery({
    queryKey: queryKeys.events.total(),
    queryFn: () => eventsApi.getAll({ page_size: 1 }),
    select: (page) => page.count,
    ...queryOptions.events.total,
  })

  const totalEvents = totalEventsCount ?? 0

  // #294 — the open critical events (the same query as the alert bar), pinned above the recent rows; from
  // their own list, so an older one below the 20 rows is never lost. Acknowledged ones stay in time order.
  const { data: criticals } = useOpenCriticals()
  const openCriticals = criticals?.open
  const acknowledge = useAcknowledgeCritical()
  const eventTitle = useEventTitle()

  // #278 — the rows open the Events page's popup in place; it pages through these same events.
  // #406 — then the Suspicious block: the unseen ones (10 rows from sm, 5 on a phone, phoneCards below), out of the recent
  // rows while they are in it. A row left beyond the cap stays a normal row; one that is seen goes back to time order.
  const { data: suspicious } = useOpenSuspicious()
  const markSeen = useMarkSeen()
  // #408 — without beta no block, also when a live message seeded the cache
  const suspiciousBeta = useSuspiciousBeta()
  const openSuspicious = suspiciousBeta ? suspicious?.open : undefined
  const suspiciousCap = phoneCards ? 5 : 10
  const { recentEvents, pinnedCount, suspiciousRowCount } = useMemo(() => {
    const pinned = pinOpenCriticals(openCriticals ?? [], eventsData?.results?.slice(0, 20) ?? [])
    const firstNormal = pinned.findIndex((e) => !isOpenCritical(e))
    const count = firstNormal === -1 ? pinned.length : firstNormal
    const block = (openSuspicious ?? []).slice(0, suspiciousCap)
    const inBlock = new Set(block.map((e) => e.id))
    return {
      recentEvents: [...pinned.slice(0, count), ...block, ...pinned.slice(count).filter((e) => !inBlock.has(e.id))],
      pinnedCount: count,
      suspiciousRowCount: block.length,
    }
  }, [openCriticals, eventsData, openSuspicious, suspiciousCap])
  const justArrived = useJustArrived(
    recentEvents.filter(isOpenCritical).map((e) => e.id),
    criticals !== undefined && eventsData !== undefined,
  )
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null)

  // #337 — the push mutes, from the same cached queries the live alerts read (the provider polls them; no second
  // timer here): a silenced row gets the muted mark. A REST event names its camera `camera`; the mutes read
  // `camera_id` (as WebSocketProvider maps it for the events it fetches).
  // CP 391 — and the person's own camera rows ("My phone alerts"), as the live bell reads them
  const { prefs: pushPrefs, cameraFilters, mutedCameraIds, myCameraFilters } = usePushMutes({ poll: false })

  // Lightweight map pins — separate dedicated endpoint, no pagination.
  // Uses the same query key as PersistentDashboardMap so React Query
  // deduplicates the network request (one HTTP call shared).
  const { data: mapPins = [], isSuccess: pinsLoaded } = useQuery({
    queryKey: queryKeys.cameras.mapPins(),
    queryFn: () => camerasApi.getMapPins(),
    ...queryOptions.cameras.mapPins,  // #336 — this observer polls every minute and on focus (the persistent map's does not)
  })

  // Register the onMarkerClick handler with the persistent map context.
  // #247 — the filter bar narrows the fleet map; the persistent map is told which ids to show.
  const visiblePins = useMemo(() => filterPins(mapPins, pinFilter), [mapPins, pinFilter])
  const filtering = isFiltering(pinFilter)
  useEffect(() => {
    setVisibleIds(filtering ? visiblePins.map((p) => p.id) : null)
  }, [setVisibleIds, filtering, visiblePins])
  useEffect(() => () => setVisibleIds(null), [setVisibleIds])
  // #290 — a restored group that is gone (renamed, deleted, emptied) would hide every pin and the
  // group list could not clear it: drop it once the pins are in. Not an edit: the view keeps its hold.
  useEffect(() => {
    if (!pinsLoaded || pinFilter.group === null || groupsOf(mapPins).includes(pinFilter.group)) return
    const next = { ...pinFilter, group: null }
    setPinFilter(next)
    saveMapFilter(next)
  }, [pinsLoaded, mapPins, pinFilter])
  // #282 — the pin of the camera whose dialog is open shows its enlarged state. (Optional call:
  // older test doubles of the context do not provide the setter.)
  useEffect(() => { setSelectedId?.(selectedPin?.id ?? null) }, [setSelectedId, selectedPin])
  useEffect(() => () => setSelectedId?.(null), [setSelectedId])

  // We keep mapPins in a ref so the handler closure always reads the latest
  // list without re-subscribing the handler on every mapPins reference change.
  // (Earlier version put mapPins in deps — but the default `mapPins = []`
  // returns a fresh array reference on every pre-load render, which caused
  // a setState-in-useEffect infinite loop. The ref pattern fixes that.)
  const mapPinsRef = useRef(mapPins)
  useEffect(() => {
    mapPinsRef.current = mapPins
  }, [mapPins])

  useEffect(() => {
    setOnMarkerClick((id: number) => {
      const pin = mapPinsRef.current.find(p => p.id === id)
      if (pin) setSelectedPin(pin)
    })
    return () => setOnMarkerClick(null)
  }, [setOnMarkerClick])

  // Clear the anchor on unmount so PersistentDashboardMap renders null while
  // the user is on other pages (portal target gone, MapView instance preserved).
  useEffect(() => {
    return () => setAnchor(null)
  }, [setAnchor])

  const DELAYS = ['delay-75', 'delay-150', 'delay-225', 'delay-300', 'delay-375']
  // #368 — "Expand map": the map card alone on the panel (from xl); kept in this browser
  const mapExpanded = useDisplayPrefs((s) => s.dashboardMapExpanded)
  const setMapExpanded = useDisplayPrefs((s) => s.setDashboardMapExpanded)

  // #413 (final review M7) — a Suspicious row's muted mark goes into the row's content area (under the band), 4 px inside
  // the picture's top-left corner. Open: the picture starts at the area's top padding (on a phone Seen wraps under it, so
  // the middle is not the picture's); seen: the middle, as on the Regular rows.
  const suspiciousMark = (event: Event) => (
    <MutedMark
      reason={muteReason({ ...event, camera_id: event.camera }, pushPrefs, cameraFilters, mutedCameraIds, myCameraFilters)}
      className={cn('left-[18px]', isOpenSuspicious(event) && 'top-3.5 sm:top-4')}
    />
  )

  // #406 — one row of Recent Events (pinned critical or normal), by its place in `recentEvents`
  const renderRecentRow = (event: Event, i: number) => (
    // #283 — hover / focus shows a preview card; the click below is unchanged
    <RecentEventPreview key={event.id} event={event} onActivate={() => setSelectedEventIndex(i)}>
    {(row) => isCriticalEvent(event) ? (
    // #294 — red / grey critical row: opening and acknowledging are two separate buttons
    <CriticalEventRow
      event={event}
      row={row}
      onOpen={() => setSelectedEventIndex(i)}
      onAcknowledge={() => acknowledge.mutate(event.id)}
      acknowledging={acknowledge.isPending && acknowledge.variables === event.id}
      justArrived={justArrived.has(event.id)}
    />
    ) : suspiciousBeta && isSuspiciousEvent(event) && !isOpenSuspicious(event) ? (
    // #413 (final review I3) — a seen Suspicious event, back in time order: the muted Suspicious row of the block (no
    // Seen button). Critical still wins (above); an open one beyond the block's cap stays a Regular row (#406)
    <div className="relative">
      <SuspiciousEventRow
        event={event}
        row={row}
        mark={suspiciousMark(event)}
        onOpen={() => setSelectedEventIndex(i)}
        onSeen={() => markSeen.mutate(event.id)}
        seeing={markSeen.isPending && markSeen.variables === event.id}
      />
    </div>
    ) : (
    // #337 (fix round 1) — the row button and, beside it, the muted mark (never inside the button)
    <AlertRowShell eventId={event.id}>
    <MutedMark
      reason={muteReason({ ...event, camera_id: event.camera }, pushPrefs, cameraFilters, mutedCameraIds, myCameraFilters)}
      className={cn('animate-fade-up motion-reduce:animate-none', DELAYS[Math.min(i, DELAYS.length - 1)])}
    />
    <button
      type="button"
      {...row}
      onClick={() => { row.onClick(); setSelectedEventIndex(i) }}
      data-unread={event.is_unread || undefined}
      className={cn(
        // #301 R7 — a little more room for the bigger content
        'relative flex w-full items-center gap-3 sm:gap-4 rounded-lg px-2.5 py-2.5 sm:py-3 text-left',
        // #301 R3 — opening fades the tint and the edge
        'hover:bg-muted/60 transition-[background-color,box-shadow] duration-200 cursor-pointer',
        // #413 — unread is the blue dot and the bold title below; no tint, no edge, no light
        // #289 — the row whose card is open stays marked: tint + a 3 px brand bar on its left
        'data-[preview-open]:bg-muted/60 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-primary before:opacity-0 before:transition-opacity before:duration-150 data-[preview-open]:before:opacity-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        'animate-fade-up motion-reduce:animate-none',
        DELAYS[Math.min(i, DELAYS.length - 1)],
      )}
    >
      {/* #278 — thumbnail; the icon stays for an event without one. #301 R7 — 96x64, 120x80 from sm. */}
      {event.thumbnail_url ? (
        <img
          src={event.thumbnail_url}
          alt={event.event_type}
          loading="lazy"
          onError={onRecentThumbError}
          className="h-16 w-24 sm:h-20 sm:w-[120px] flex-shrink-0 rounded-md object-cover bg-muted"
        />
      ) : (
        <div
          data-testid="recent-event-thumb-placeholder"
          className="flex h-16 w-24 sm:h-20 sm:w-[120px] flex-shrink-0 items-center justify-center rounded-md bg-amber-500/10"
        >
          <Activity className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
      )}

      {/* Event info — #284: three full-width lines. The exact time (#278, item 272 format) sits on
          the title line instead of its own column; the group joins the camera line. */}
      <div className="flex-1 min-w-0">
        {/* #301 review — on phones the time goes under the title so the title keeps the width */}
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-0.5 sm:gap-3">
          {/* #327 — on phones the title wraps instead of ending in "…" (the time is under it since #301); from sm, where it shares the line with the time, it is cut as before (#284) */}
          <p className={cn('text-[15px] sm:text-base leading-snug break-words sm:truncate', event.is_unread ? 'font-bold' : 'font-semibold')}>
            {/* #413 — before the type, so a long type that is cut off never hides it */}
            {event.is_unread && <UnreadDot className="mr-1.5 align-[1px]" />}
            {eventTitle(event)}
          </p>
          <span
            className="text-xs sm:text-[13px] text-muted-foreground whitespace-nowrap tabular-nums flex-shrink-0"
            title={new Date(event.occurred_at).toLocaleString()}
          >
            {formatCardDateTime(event.occurred_at)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <Camera className="h-4 w-4 text-muted-foreground/60 flex-shrink-0" />
          <p className="text-[13px] sm:text-sm text-muted-foreground leading-snug truncate">
            <span>{event.camera_name}</span>
            {event.camera_group && <span> · {event.camera_group}</span>}
          </p>
          {event.detected_objects && event.detected_objects.length > 0 && (
            <div className="flex gap-1 flex-shrink-0 ml-1">
              {event.detected_objects.slice(0, 2).map((obj) => (
                <span
                  key={obj}
                  className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0 text-[11px] sm:text-xs font-medium text-primary"
                >
                  {tc(`detectionClasses.${obj}`, { defaultValue: obj.replace(/_/g, ' ') })}
                </span>
              ))}
              {event.detected_objects.length > 2 && (
                <span className="text-[11px] sm:text-xs text-muted-foreground">
                  +{event.detected_objects.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        {/* #278 — AI caption (item 109); only when the event has one */}
        {event.activity_caption && (
          <div className="flex items-start gap-1.5 mt-1">
            <Sparkles className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden />
            <p className="text-[13px] sm:text-sm text-muted-foreground leading-snug line-clamp-2">{event.activity_caption}</p>
          </div>
        )}
      </div>
    </button>
    </AlertRowShell>
    )}
    </RecentEventPreview>
  )

  return (
    // #368 — from xl the page is a flex column: the stats strip, then the panel takes what is left under it and
    // under whatever bars show above the page (banner, sound bar, alert bar), so the page never scrolls for them.
    // `xl:h-0 xl:min-h-full`, NOT `xl:h-full`: the padded wrapper in MainLayout is a flex item whose minimum height
    // follows its content, and under that content measurement the grid row sizes to the whole events list; with
    // h-full the page followed the grown wrapper and the main area scrolled (owner, stage 2026-10-21). A height of 0
    // keeps the page out of that measurement; min-height 100 % then fills the wrapper once it is laid out.
    <div className="space-y-4 sm:space-y-6 xl:space-y-0 xl:flex xl:h-0 xl:min-h-full xl:flex-col xl:gap-4">

      {/* #368 — from xl: one strip; below xl: the three cards. One wrapper around both: below xl the hidden strip is
          still an element child and `space-y` would give the cards a margin they never had (it skips the [hidden]
          attribute, not the hidden class); from xl the wrapper dissolves (`contents`) and strip, cards and panel are
          the flex column's own items. */}
      <div className="xl:contents">
      <StatsStrip totalCameras={totalCameras} camerasLoading={camerasLoading} totalEvents={totalEvents} totalLoading={totalLoading} hidden={mapExpanded} />

      {/* Stats Row — 3 compact metric cards (phones and tablets; #368 hides them from xl) */}
      <div className={phoneCards ? 'grid gap-2 grid-cols-1 xl:hidden' : 'grid gap-3 grid-cols-2 sm:grid-cols-3 xl:hidden'}>
        <StatCard
          title={t('dashboard.totalCameras')}
          value={totalCameras}
          description={t('dashboard.registered', { count: totalCameras })}
          icon={<Camera className="h-4 w-4" />}
          accent="bg-primary/10"
          iconColor="text-primary"
          topBorder="bg-primary"
          loading={camerasLoading}
          delay="delay-75"
          slim={phoneCards}
        />
        {/* #292 — cards: Total Cameras · Recent Events (unread) · Total Events */}
        <UnreadEventsCard slim={phoneCards} />
        <StatCard
          title={t('dashboard.totalEvents')}
          value={totalEvents}
          description={t('dashboard.allTimeDetections')}
          icon={<Activity className="h-4 w-4" />}
          accent="bg-amber-500/10"
          iconColor="text-amber-600 dark:text-amber-400"
          topBorder="bg-amber-500"
          loading={totalLoading}
          delay="delay-225"
          slim={phoneCards}
        />
      </div>
      </div>

      {/* Main panel — Recent Events (left) + Camera Map (right), equal halves from xl (#284; was 3/5 + 2/5
          from lg: at 1024-1279 px with the sidebar open each half would be only ~344 px, so the panels stay
          stacked there). Height from xl (#368): the rest of the page as a flex item (was a 22rem formula, wrong by
          every bar above the page). `xl:min-h-[360px]` replaces the flex item's content-based min-height, so the
          panel can shrink to what is left; on a window too short for it the page scrolls instead. */}
      <div className={cn('grid gap-4 animate-fade-up delay-300 min-w-0 xl:flex-1 xl:min-h-[360px]', mapExpanded ? 'xl:grid-cols-1' : 'xl:grid-cols-2')}>

        {/* Recent Events — half from xl, full width below */}
        <Card className={cn('min-w-0 overflow-hidden flex flex-col h-[420px] sm:h-[480px] xl:h-full', mapExpanded && 'xl:hidden')}>{/* #368 — hidden while the map is expanded */}
          <CardHeader className="px-4 pt-3 pb-2">{/* #368 — compact */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold">{te('title')}</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                {/* #304 — every event, the same number as the Total Events card */}
                {totalEventsCount !== undefined && totalEventsCount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {totalEventsCount.toLocaleString()} {te('total')}
                  </span>
                )}
                <Link
                  to="/events"
                  className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {te('viewAll', { defaultValue: 'View all →' })}
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto px-4 pt-1 pb-4">
              {eventsLoading ? (
                <div className="space-y-2">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 sm:gap-4 rounded-lg px-2.5 py-2.5 sm:py-3">
                      <div className="shimmer h-16 w-24 sm:h-20 sm:w-[120px] rounded-md flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="flex justify-between gap-3">
                          <div className="shimmer h-3.5 w-32 rounded" />
                          <div className="shimmer h-3 w-16 rounded" />
                        </div>
                        <div className="shimmer h-3 w-40 rounded" />
                        <div className="shimmer h-3 w-3/4 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentEvents.length > 0 ? (
                <div className="space-y-0.5">
                  {recentEvents.slice(0, pinnedCount).map((event, i) => renderRecentRow(event, i))}
                  {/* #406 — the Suspicious block; not there at all while nothing is unseen */}
                  {suspiciousRowCount > 0 && (
                    <div data-suspicious-block role="group" aria-label={tc('suspicious.label')} className="my-1">
                      <p className="sus-label px-0.5 pt-1.5 pb-0.5">{tc('suspicious.block', { count: suspicious?.count ?? suspiciousRowCount })}</p>
                      {recentEvents.slice(pinnedCount, pinnedCount + suspiciousRowCount).map((event, k) => (
                        <RecentEventPreview key={event.id} event={event} onActivate={() => setSelectedEventIndex(pinnedCount + k)}>
                          {(row) => (
                            <div className="relative">
                              <SuspiciousEventRow
                                event={event}
                                row={row}
                                mark={suspiciousMark(event)}
                                onOpen={() => setSelectedEventIndex(pinnedCount + k)}
                                onSeen={() => markSeen.mutate(event.id)}
                                seeing={markSeen.isPending && markSeen.variables === event.id}
                              />
                            </div>
                          )}
                        </RecentEventPreview>
                      ))}
                      <Link
                        to="/events?category=suspicious"
                        className="sus-see-all inline-flex min-h-11 sm:min-h-0 items-center px-0.5 sm:py-1 text-xs font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                      >
                        {tc('suspicious.seeAll')}
                      </Link>
                    </div>
                  )}
                  {recentEvents.slice(pinnedCount + suspiciousRowCount).map((event, k) => renderRecentRow(event, pinnedCount + suspiciousRowCount + k))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center gap-1 text-center">
                  <Activity className="h-6 w-6 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{te('noRecentEvents')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Camera Map — half from xl (#284), full width below */}
        {/* #324 — below xl the map has the fixed height, not the card: the filter bar may wrap without shrinking the map */}
        <Card className="border-border/60 overflow-hidden min-w-0 flex flex-col xl:h-full">
          <CardHeader className="px-4 pt-3 pb-2">{/* #368 — compact */}
            <div className="flex flex-wrap items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-semibold">{t('map.title')}</CardTitle>
              {mapPins.length > 0 && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {filtering ? t('map.shown', { shown: visiblePins.length, total: mapPins.length }) : t('map.mapped', { count: mapPins.length })}
                </span>
              )}
              {/* #368 — the Google map / Site map switch (#363) renders here, portalled by PersistentDashboardMap, when the
                  user's groups have a drawing; a callback ref, so React publishes the node on mount and null on unmount */}
              <div ref={setModeAnchor} className="flex items-center empty:hidden" />
              {/* #368 — from xl only (below it the cards stack, so it has no meaning); the map keeps its view (#290) */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('hidden xl:inline-flex h-7 px-2 text-xs', mapPins.length === 0 && 'ml-auto')}
                onClick={() => setMapExpanded(!mapExpanded)}
              >
                {mapExpanded ? <Minimize2 className="mr-1 h-3.5 w-3.5" /> : <Maximize2 className="mr-1 h-3.5 w-3.5" />}
                {t(mapExpanded ? 'map.collapse' : 'map.expand')}
              </Button>
            </div>
            {/* #247 — status chips, group select and search for the fleet map */}
            {mapPins.length > 0 && (
              <div className="mt-2">
                <FleetMapFilters pins={mapPins} value={pinFilter} onChange={editPinFilter} settings={mapSettings} />
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            {mapPins.length > 0 ? (
              // Anchor div: PersistentDashboardMap portals <MapView> into this node.
              // Using `ref={setAnchor}` as a callback ref so React calls setAnchor
              // with the DOM element on mount and null on unmount automatically.
              // The explicit useEffect cleanup above handles the unmount case too,
              // ensuring the anchor is cleared when DashboardPage unmounts.
              <div
                ref={setAnchor}
                data-testid="dashboard-map-anchor"
                className="h-[320px] sm:h-[380px] xl:h-full min-h-0 w-full overflow-hidden rounded-b-xl"
              />
            ) : (
              <div className="h-[320px] sm:h-[380px] xl:h-full min-h-0 flex flex-col items-center justify-center gap-2 text-center">
                <MapPin className="h-8 w-8 text-muted-foreground/20" />
                <p className="text-sm text-muted-foreground">{t('map.noCoordinates')}</p>
                <p className="text-xs text-muted-foreground/60">{t('map.addCoordinatesHint')}</p>
                <Link
                  to="/cameras"
                  className="mt-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {t('map.goToCameras', { defaultValue: 'Go to Cameras →' })}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* #278 — event popup, opened in place from a recent-events row */}
      <EventDetailModal
        events={recentEvents}
        selectedIndex={selectedEventIndex}
        onClose={() => setSelectedEventIndex(null)}
        onNavigate={setSelectedEventIndex}
      />

      {/* Camera Pin Detail Modal */}
      {selectedPin && (() => {
        const lat = parseFloat(selectedPin.latitude)
        const lng = parseFloat(selectedPin.longitude)
        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
        return (
          <Dialog.Root open={!!selectedPin} onOpenChange={(open) => { if (!open) setSelectedPin(null) }}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
              <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <Dialog.Title className="sr-only">{selectedPin.name}</Dialog.Title>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <Camera className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{selectedPin.name}</p>
                      {selectedPin.location && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <p className="text-xs text-muted-foreground truncate">{selectedPin.location}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Dialog.Close asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <X className="h-4 w-4" />
                    </Button>
                  </Dialog.Close>
                </div>

                {/* Details */}
                <div className="px-5 py-4 space-y-3">
                  {/* Group */}
                  {selectedPin.group_names.length > 0 && (
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{selectedPin.group_names.join(', ')}</p>
                    </div>
                  )}

                  {/* #247 — address, Plus Code, coordinates, directions (the footer keeps Open in Google Maps) */}
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <LocationDetails lat={lat} lng={lng} address={selectedPin.location_address} openInMaps={false} />
                  </div>

                  {/* Last Event */}
                  {selectedPin.last_event_type && selectedPin.last_event_at && (
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium">
                          {tc(`eventTypes.${selectedPin.last_event_type}`, { defaultValue: selectedPin.last_event_type.replace(/_/g, ' ') })}
                        </p>
                        <p className="text-xs text-muted-foreground">{relativeTime(selectedPin.last_event_at)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1 h-9 text-xs gap-1.5"
                    onClick={() => {
                      if (selectedPin.is_locked) {  // #077 — SIM mismatch: locked
                        toast.error('Camera locked', { description: 'SIM mismatch — under review.' })
                        return
                      }
                      setSelectedPin(null)
                      navigate(`/events?camera=${selectedPin.id}&category=${selectedPin.default_event_category ?? 'animal'}`, {
                        state: { from: { pathname: '/', search: '', labelKey: 'nav.dashboard' } },
                      })
                    }}
                  >
                    <Activity className="h-3.5 w-3.5" />
                    {te('viewAll')}
                  </Button>
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" size="sm" className="w-full h-9 text-xs gap-1.5">
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t('map.openInMaps')}
                    </Button>
                  </a>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )
      })()}
    </div>
  )
}
